import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Chat } from "src/lib";
import { useIsMobile } from "src/lib/hooks/useIsMobile";
import { useDrawerStore } from "src/store/drawerStore";
import { useMessageStore } from "src/store/messageStore";
import { useRankingStore } from "src/store/rankingStore";
import { ChatMessageRow, ChatMessageItem } from "./ChatMessageRow";
import {
    computeTurnLayout,
    scheduleScrollUserMessageToTop,
} from "./scrollUserMessageForReply";

const LateralChat = () => {
    const isMobile = useIsMobile();
    const open = useDrawerStore((s: any) => s.open);
    const toggle = useDrawerStore((s: any) => s.toggle);
    const isFullscreen = useDrawerStore((s: any) => s.isFullscreen);
    const setFullscreen = useDrawerStore((s: any) => s.setFullscreen);
    const toggleFullscreen = useDrawerStore((s: any) => s.toggleFullscreen);
    const clearRankings = useRankingStore((s: any) => s.clearRankings);
    const msgContext = useMessageStore((s: any) => s.actualContext);
    const messages = useMessageStore((s: any) => s.messages) as ChatMessageItem[];
    const isStreaming = useMessageStore((s: any) => s.isStreaming);
    const setActualContext = useMessageStore((s: any) => s.setActualContext);

    const contentRef = useRef<HTMLDivElement | null>(null);
    const bottomAreaRef = useRef<HTMLDivElement | null>(null);
    const lastMessageCountRef = useRef(0);
    const scrolledForTurnRef = useRef<string | null>(null);
    const pendingScrollUserIdRef = useRef<string | null>(null);
    const [streamingMinHeights, setStreamingMinHeights] = useState<Record<string, number>>({});
    const [bottomSpacerHeight, setBottomSpacerHeight] = useState<number | undefined>();
    // BottomArea floats over Content on purpose (its "frosted glass" blur needs real content behind it
    // to blur) — so instead of it taking up its own flex space, we measure its actual rendered height
    // here and feed it back as Content's own bottom padding, so it never permanently covers the last message.
    // Starts at a sensible guess (roughly the plain single-line composer) so there's no visible jump
    // before the ResizeObserver below gets its first real measurement.
    const [bottomAreaHeight, setBottomAreaHeight] = useState(78);
    // Set right before each height change, from whether the user was already at the bottom at that
    // moment — read by the effect below, once the new padding has actually landed in the DOM, to decide
    // whether to follow the growth down. Someone scrolled up reading old messages is left alone.
    const wasNearBottomRef = useRef(true);

    useEffect(() => {
        const el = bottomAreaRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            const contentEl = contentRef.current;
            wasNearBottomRef.current = contentEl
                ? contentEl.scrollHeight - contentEl.scrollTop - contentEl.clientHeight < 40
                : false;
            setBottomAreaHeight(entry.contentRect.height);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const contentEl = contentRef.current;
        if (!contentEl || !wasNearBottomRef.current) return;
        contentEl.scrollTo({ top: contentEl.scrollHeight, behavior: "auto" });
    }, [bottomAreaHeight]);

    useEffect(() => {
        if (!isStreaming) {
            scrolledForTurnRef.current = null;
            pendingScrollUserIdRef.current = null;
            setBottomSpacerHeight(undefined);
            return;
        }

        const assistant = [...messages].reverse().find((m) => m.answer && m.status === "streaming");
        if (!assistant || scrolledForTurnRef.current === assistant.id) return;

        const assistantIndex = messages.findIndex((m) => m.id === assistant.id);
        const userMsg = assistantIndex > 0 ? messages[assistantIndex - 1] : undefined;
        if (!userMsg || userMsg.answer) return;

        scrolledForTurnRef.current = assistant.id;
        const el = contentRef.current;
        if (!el) return;

        const layout = computeTurnLayout(el, userMsg.id);
        if (!layout) return;

        pendingScrollUserIdRef.current = userMsg.id;
        setStreamingMinHeights((prev) => ({ ...prev, [assistant.id]: layout.assistantMinHeight }));
        setBottomSpacerHeight(layout.bottomSpacerHeight);
    }, [messages, isStreaming]);

    useLayoutEffect(() => {
        const userId = pendingScrollUserIdRef.current;
        const el = contentRef.current;
        if (!userId || !el || bottomSpacerHeight == null) return;

        scheduleScrollUserMessageToTop(el, userId);
        pendingScrollUserIdRef.current = null;
    }, [bottomSpacerHeight, streamingMinHeights]);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const count = messages.length;
        const prevCount = lastMessageCountRef.current;
        const delta = count - prevCount;
        lastMessageCountRef.current = count;

        if (delta <= 0) return;

        if (delta > 2) {
            requestAnimationFrame(() => {
                el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
            });
        }
    }, [messages]);

    useEffect(() => {
        const doneIds = new Set(
            messages.filter((m) => m.status === "done" || m.status === "error").map((m) => m.id)
        );
        setStreamingMinHeights((prev) => {
            const next = { ...prev };
            let changed = false;
            for (const id of Object.keys(next)) {
                if (doneIds.has(id)) {
                    delete next[id];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [messages]);

    // no mobile o chat só existe maximizado: sempre que abrir, força fullscreen
    useEffect(() => {
        if (isMobile && open) {
            setFullscreen(true);
        }
    }, [isMobile, open, setFullscreen]);

    const handleCloseChat = () => {
        clearRankings();
        setActualContext(undefined);
        setFullscreen(false);
        toggle();
    };

    return (
        <Chat.Root>
            <Chat.Header
                onClose={handleCloseChat}
                onToggleFullscreen={isMobile ? undefined : toggleFullscreen}
                isFullscreen={isFullscreen}
            />
            <Chat.Content ref={contentRef} sx={{ paddingBottom: `${bottomAreaHeight + 16}px` }}>
                {messages.map((message) => (
                    <ChatMessageRow
                        key={message.id}
                        message={message}
                        streamingMinHeight={streamingMinHeights[message.id]}
                    />
                ))}
                {bottomSpacerHeight != null ? (
                    <div
                        aria-hidden
                        data-chat-scroll-spacer
                        style={{ height: bottomSpacerHeight, flexShrink: 0, pointerEvents: "none" }}
                    />
                ) : null}
            </Chat.Content>
            <Chat.BottomArea ref={bottomAreaRef} context={msgContext} />
        </Chat.Root>
    );
};

export { LateralChat };
